import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Instagram, Send, Gift, Sparkles, Handshake } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { fadeUp, viewportOnce } from "../../lib/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "himanginisingh.2002@gmail.com";
const IG = "https://instagram.com/hi._.manganese";

const OPEN_FOR = [
  { icon: Gift, label: "PR Gifting" },
  { icon: Sparkles, label: "Reels / Stories Integration" },
  { icon: Handshake, label: "Long-term Ambassadorships" },
];

const brutalField =
  "border-[3px] border-clay-ink bg-white font-body text-clay-ink shadow-brutal-sm rounded-xl h-12 focus-visible:ring-clay-violet focus-visible:ring-2";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    brand: "",
    collab_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.collab_type || !form.message) {
      toast.error("Please fill in name, email, collab type and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/collab`, form);
      toast.success("Sent! Himangini will get back to you soon.");
      setForm({ name: "", email: "", brand: "", collab_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-4 py-20 sm:px-6 sm:py-28">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Left */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="flex flex-col justify-between rounded-[2.5rem] border-[3px] border-clay-ink bg-clay-violet p-8 text-white shadow-clay lg:col-span-5"
        >
          <div>
            <span className="font-body text-sm font-bold uppercase tracking-widest text-clay-lime">
              Let&apos;s collab
            </span>
            <h2 className="mt-3 font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-5xl">
              Ready to make something that sticks?
            </h2>
            <p className="mt-4 font-body text-sm font-medium text-white/80">
              Open for PR gifting, product integrations in Reels & Stories, and
              long-term brand ambassadorships.
            </p>

            <div className="mt-8 space-y-3">
              {OPEN_FOR.map((o) => (
                <div key={o.label} className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-clay-ink bg-clay-lime">
                    <o.icon className="h-4 w-4 text-clay-ink" strokeWidth={2.5} />
                  </span>
                  <span className="font-body text-sm font-bold">{o.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 space-y-3">
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 rounded-xl border-[3px] border-clay-ink bg-white px-4 py-3 font-body text-sm font-bold text-clay-ink shadow-brutal-sm transition-transform hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              <Mail className="h-4 w-4 shrink-0" strokeWidth={2.5} />
              <span className="truncate">{EMAIL}</span>
            </a>
            <a
              href={IG}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-3 rounded-xl border-[3px] border-clay-ink bg-clay-lime px-4 py-3 font-body text-sm font-bold text-clay-ink shadow-brutal-sm transition-transform hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              <Instagram className="h-4 w-4 shrink-0" strokeWidth={2.5} />
              instagram.com/hi._.manganese
            </a>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          data-testid="collab-form"
          variants={fadeUp}
          custom={1}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="rounded-[2.5rem] border-[3px] border-clay-ink bg-white p-8 shadow-clay lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="font-body font-bold text-clay-ink">Name *</Label>
              <Input data-testid="input-name" className={brutalField} placeholder="Your name" value={form.name} onChange={set("name")} />
            </div>
            <div className="space-y-2">
              <Label className="font-body font-bold text-clay-ink">Email *</Label>
              <Input data-testid="input-email" type="email" className={brutalField} placeholder="you@brand.com" value={form.email} onChange={set("email")} />
            </div>
            <div className="space-y-2">
              <Label className="font-body font-bold text-clay-ink">Brand / Company</Label>
              <Input data-testid="input-brand" className={brutalField} placeholder="Optional" value={form.brand} onChange={set("brand")} />
            </div>
            <div className="space-y-2">
              <Label className="font-body font-bold text-clay-ink">Collab type *</Label>
              <Select value={form.collab_type} onValueChange={(v) => setForm((f) => ({ ...f, collab_type: v }))}>
                <SelectTrigger data-testid="select-collab-type" className={brutalField}>
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent className="border-[3px] border-clay-ink rounded-xl">
                  <SelectItem value="PR Gifting">PR Gifting</SelectItem>
                  <SelectItem value="Reels/Stories Integration">Reels / Stories Integration</SelectItem>
                  <SelectItem value="Brand Ambassadorship">Brand Ambassadorship</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label className="font-body font-bold text-clay-ink">Message *</Label>
            <Textarea data-testid="input-message" rows={5} className="border-[3px] border-clay-ink bg-white font-body text-clay-ink shadow-brutal-sm rounded-xl focus-visible:ring-clay-violet focus-visible:ring-2" placeholder="Tell me about the product & campaign..." value={form.message} onChange={set("message")} />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="collab-submit-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-clay-ink bg-clay-violet px-6 py-3.5 font-display text-base font-bold text-white shadow-clay-btn transition-transform hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send inquiry"}
            <Send className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
